import React, { useState, useEffect, useCallback } from 'react';
import { CalendarPlus, Copy, Check, ExternalLink, RefreshCw } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { AppModal, AppButton, ConfirmModal } from '../ui';

/*
 * Converted to utilities in Phase 5; connectCalendar.scss deleted in the same
 * commit (TOKEN_CONTRACT §6.1). Two things the stylesheet had wrong:
 *
 *  - The URL field's focus border was `hsl(var(--accent))`. `--accent` is
 *    `220 14% 95%` — a near-white SURFACE tint, not an accent colour — so
 *    focusing the field made its border almost disappear against the card
 *    instead of highlighting it. It is `--primary` now, like every other focus
 *    treatment in the app.
 *  - The monospace stack was written out by hand (`ui-monospace, SFMono-Regular,
 *    Menlo, monospace`) alongside a `--font-mono` token that says something
 *    slightly different. It reads the token now.
 */

/**
 * ConnectCalendarModal — one-way iCal subscription.
 *
 * Shows the user's personal feed URL (from GET /calendar/feed-url) which they
 * paste into Google Calendar / Apple Calendar / Outlook. "Regenerate" rotates
 * the per-user key on the server, invalidating any previously shared URL.
 */
const ConnectCalendarModal = ({ show, onHide, onToast }) => {
  const [loading, setLoading] = useState(false);
  const [feedUrl, setFeedUrl] = useState('');
  const [webcalUrl, setWebcalUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);

  const loadFeedUrl = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/calendar/feed-url');
      setFeedUrl(data.feedUrl || '');
      setWebcalUrl(data.webcalUrl || '');
    } catch (err) {
      onToast?.(err.response?.data?.message || 'Failed to load calendar link.', 'error');
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  useEffect(() => {
    if (show) {
      setCopied(false);
      loadFeedUrl();
    }
  }, [show, loadFeedUrl]);

  const handleCopy = async () => {
    if (!feedUrl) return;
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onToast?.('Could not copy. Select the URL and copy manually.', 'error');
    }
  };

  const handleRegenerate = async () => {
    setConfirmRegen(false);
    setLoading(true);
    try {
      // Rotating the key server-side: regenerate endpoint reuses feed-url after
      // clearing the stored key. We POST to rotate, then refetch.
      await apiClient.post('/calendar/feed-url/regenerate');
      await loadFeedUrl();
      onToast?.('Calendar link regenerated. The old link no longer works.', 'success');
    } catch (err) {
      onToast?.(err.response?.data?.message || 'Failed to regenerate link.', 'error');
      setLoading(false);
    }
  };

  return (
    <>
      <AppModal
        show={show}
        onHide={onHide}
        title="Connect your calendar"
        icon={<CalendarPlus size={18} />}
        size="md"
      >
        <div className="tw-flex tw-flex-col tw-gap-[14px]">
          <p className="tw-m-0 tw-text-[0.9rem] tw-leading-normal tw-text-content-muted">
            Subscribe from Google Calendar, Apple Calendar or Outlook to see your
            events on your phone and laptop. Updates flow one-way (from the LMS),
            refreshing automatically every few hours.
          </p>

          <label className="-tw-mb-1.5 tw-text-[0.8rem] tw-font-semibold tw-text-content">Your private subscription URL</label>
          <div className="tw-flex tw-items-stretch tw-gap-2">
            <input
              className="tw-min-w-0 tw-flex-1 tw-rounded-[8px] tw-border tw-border-line tw-bg-surface-alt tw-px-3 tw-py-[9px] tw-font-mono tw-text-[0.85rem] tw-text-content tw-outline-none focus:tw-border-primary"
              type="text"
              readOnly
              value={loading ? 'Loading…' : feedUrl}
              onFocus={(e) => e.target.select()}
            />
            <AppButton
              variant="outline"
              size="icon"
              onClick={handleCopy}
              disabled={loading || !feedUrl}
              title="Copy URL"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </AppButton>
          </div>

          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <AppButton
              as="a"
              href={webcalUrl || '#'}
              variant="default"
              size="sm"
              className={!webcalUrl ? 'app-btn--disabled' : ''}
            >
              <ExternalLink size={15} /> One-click subscribe
            </AppButton>
            <AppButton
              variant="ghost"
              size="sm"
              onClick={() => setConfirmRegen(true)}
              disabled={loading}
            >
              <RefreshCw size={15} /> Regenerate link
            </AppButton>
          </div>

          <div className="tw-rounded-[8px] tw-border tw-border-line tw-bg-surface-alt tw-px-3.5 tw-py-3 tw-text-[0.82rem] tw-leading-[1.7] tw-text-content-muted [&_strong]:tw-text-content [&_em]:tw-font-semibold [&_em]:tw-not-italic [&_em]:tw-text-content">
            <strong>Google Calendar:</strong> Other calendars → <em>From URL</em> → paste the URL above.
            <br />
            <strong>Apple Calendar:</strong> File → <em>New Calendar Subscription</em> → paste the URL.
            <br />
            <strong>Outlook:</strong> Add calendar → <em>Subscribe from web</em> → paste the URL.
          </div>

          <p className="tw-m-0 tw-text-[0.78rem] tw-text-content-muted">
            Keep this URL private — anyone with it can view your calendar. Use
            “Regenerate link” if it’s ever exposed.
          </p>
        </div>
      </AppModal>

      <ConfirmModal
        show={confirmRegen}
        onHide={() => setConfirmRegen(false)}
        onConfirm={handleRegenerate}
        title="Regenerate calendar link?"
        message="Your current subscription URL will stop working immediately. You’ll need to re-subscribe with the new URL in each calendar app."
        confirmText="Regenerate"
        variant="danger"
        loading={loading}
      />
    </>
  );
};

export default ConnectCalendarModal;
